import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { useAuth } from '../../../components/auth/AuthProvider'
import { AdminPanelButton } from './AdminPanelButton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert'

type TierFilter = 'all' | 'explorer' | 'builder' | 'pioneer'
type StatusFilter = 'all' | 'at_limit' | 'near_limit' | 'active' | 'idle'
type SortKey = 'usage' | 'messages' | 'cost' | 'name'

interface CreditUserRow {
  user_id: string
  email: string
  username: string
  first_name?: string
  last_name?: string
  is_active: boolean
  credit_tier: string
  credit_tier_label: string
  monthly_user_messages_used: number
  monthly_message_limit: number | null
  message_utilization_pct: number | null
  monthly_cost_usd: number
  monthly_cost_usd_limit: number | null
  cost_utilization_pct: number | null
  credit_limit_exceeded: boolean
  message_quota_exceeded: boolean
  cost_quota_exceeded: boolean
  status: 'ok' | 'near_limit' | 'at_limit' | 'idle' | 'unlimited'
}

interface TierSummary {
  tier_id: string
  tier_label: string
  user_count: number
  users_at_limit: number
  users_near_limit: number
  users_with_usage: number
  total_messages_used: number
  total_cost_usd: number
  default_message_limit: number | null
  default_cost_usd_limit: number | null
}

interface CreditMetrics {
  billing_month: string
  by_tier: TierSummary[]
  users: CreditUserRow[]
}

const TIER_ORDER: TierFilter[] = ['explorer', 'builder', 'pioneer']

const TIERS: { value: TierFilter; label: string }[] = [
  { value: 'all', label: 'All tiers' },
  { value: 'explorer', label: 'Explorer' },
  { value: 'builder', label: 'Builder' },
  { value: 'pioneer', label: 'Pioneer' },
]

const STATUSES: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'at_limit', label: 'At limit' },
  { value: 'near_limit', label: 'Near limit' },
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'No usage' },
]

const TIER_COLORS: Record<string, string> = {
  explorer: 'var(--muted)',
  builder: '#60A5FA',
  pioneer: 'var(--gold)',
}

const card: React.CSSProperties = {
  background: 'var(--white)',
  border: '1px solid var(--bd)',
  borderRadius: 12,
  boxShadow: 'var(--sh-sm)',
}

function getDisplayName(user: CreditUserRow): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || user.username
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value)
}

function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym
  return new Intl.DateTimeFormat('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, 1))
  )
}

function buildMonthOptions(): { value: string; label: string }[] {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date())
  const y = Number(parts.find((p) => p.type === 'year')?.value)
  const m = Number(parts.find((p) => p.type === 'month')?.value)
  const options: { value: string; label: string }[] = []
  let year = y
  let month = m
  for (let i = 0; i < 12; i++) {
    const value = `${year}-${String(month).padStart(2, '0')}`
    options.push({ value, label: formatMonthLabel(value) })
    month -= 1
    if (month < 1) {
      month = 12
      year -= 1
    }
  }
  return options
}

function matchesStatus(user: CreditUserRow, status: StatusFilter): boolean {
  if (status === 'all') return true
  if (status === 'at_limit') return user.credit_limit_exceeded
  if (status === 'near_limit') return user.status === 'near_limit'
  if (status === 'active') return user.status !== 'idle'
  return user.status === 'idle'
}

function computeSummary(users: CreditUserRow[]) {
  let active = 0
  let atLimit = 0
  let nearLimit = 0
  let totalCost = 0
  for (const u of users) {
    totalCost += u.monthly_cost_usd
    if (u.monthly_user_messages_used > 0 || u.monthly_cost_usd > 0) active += 1
    if (u.credit_limit_exceeded) atLimit += 1
    if (u.status === 'near_limit') nearLimit += 1
  }
  return { total: users.length, active, atLimit, nearLimit, totalCost }
}

function sortByTierOrder(rows: TierSummary[]): TierSummary[] {
  const byId = new Map(rows.map((r) => [r.tier_id, r]))
  return TIER_ORDER.map((id) => byId.get(id)).filter((r): r is TierSummary => r != null)
}

function computeByTier(users: CreditUserRow[], defaults: TierSummary[]): TierSummary[] {
  const map = new Map(
    defaults.map((t) => [
      t.tier_id,
      { ...t, user_count: 0, users_at_limit: 0, users_near_limit: 0, users_with_usage: 0, total_messages_used: 0, total_cost_usd: 0 },
    ])
  )
  for (const u of users) {
    const row = map.get(u.credit_tier)
    if (!row) continue
    row.user_count += 1
    row.total_messages_used += u.monthly_user_messages_used
    row.total_cost_usd += u.monthly_cost_usd
    if (u.monthly_user_messages_used > 0 || u.monthly_cost_usd > 0) row.users_with_usage += 1
    if (u.credit_limit_exceeded) row.users_at_limit += 1
    if (u.status === 'near_limit') row.users_near_limit += 1
  }
  return sortByTierOrder(Array.from(map.values()))
}

const MONTH_OPTIONS = buildMonthOptions()
const DEFAULT_MONTH = MONTH_OPTIONS[0]?.value ?? ''
const USERS_PER_PAGE = 10

function scrollPageToTop(anchor: HTMLElement | null) {
  if (!anchor) return
  let parent = anchor.parentElement
  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      parent.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    parent = parent.parentElement
  }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const UsageBlock = ({
  label,
  used,
  limit,
  pct,
  danger,
  formatValue,
}: {
  label: string
  used: number
  limit: number | null
  pct: number | null
  danger?: boolean
  formatValue: (n: number) => string
}) => {
  const usedLabel = formatValue(used)
  const limitLabel = limit == null ? 'No limit' : formatValue(limit)
  const width = pct != null ? Math.min(100, Math.max(0, pct)) : null
  const barColor = danger ? '#F87171' : width != null && width >= 80 ? '#FBBF24' : 'var(--gold)'
  const pctLabel = pct != null ? `${Math.round(pct)}%` : null

  return (
    <div style={{ minWidth: 0, padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, letterSpacing: '0.02em' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: width != null ? 8 : 0 }}>
        <div style={{ lineHeight: 1.3 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{usedLabel}</span>
          <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 6 }}>of {limitLabel}</span>
        </div>
        {pctLabel && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: danger ? '#F87171' : width != null && width >= 80 ? '#D97706' : 'var(--muted)',
              flexShrink: 0,
            }}
          >
            {pctLabel}
          </span>
        )}
      </div>
      {width != null && (
        <div style={{ height: 8, background: 'var(--white)', borderRadius: 100, overflow: 'hidden', border: '1px solid var(--bd2)' }}>
          <div style={{ width: `${width}%`, height: '100%', background: barColor, borderRadius: 100, transition: 'width .2s ease' }} />
        </div>
      )}
    </div>
  )
}

export const CreditSystemDashboard = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [baseMetrics, setBaseMetrics] = useState<CreditMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [month, setMonth] = useState(DEFAULT_MONTH)
  const [tier, setTier] = useState<TierFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<SortKey>('usage')
  const [page, setPage] = useState(1)
  const scrollAnchorRef = useRef<HTMLDivElement>(null)
  const skipPageScrollRef = useRef(true)

  const fetchMetrics = useCallback(async () => {
    if (!user?.email) return
    setLoading(true)
    setFetchError(null)
    try {
      const params = new URLSearchParams({ tier: 'all', status: 'all' })
      if (month) params.set('month', month)
      if (includeInactive) params.set('include_inactive', 'true')
      const response = await fetch(`${API_BASE_URL}admin/credit-system/metrics?${params}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-user-email': user.email },
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || err.detail || `HTTP ${response.status}`)
      }
      setBaseMetrics(await response.json())
    } catch (error) {
      setFetchError(error instanceof Error ? error.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [month, includeInactive, user?.email])

  useEffect(() => {
    void fetchMetrics()
  }, [fetchMetrics])

  const filtered = useMemo(() => {
    let list = (baseMetrics?.users ?? []).filter((u) => {
      if (tier !== 'all' && u.credit_tier !== tier) return false
      return matchesStatus(u, status)
    })
    const term = search.trim().toLowerCase()
    if (term) {
      list = list.filter(
        (u) =>
          u.email.toLowerCase().includes(term) ||
          u.username.toLowerCase().includes(term) ||
          getDisplayName(u).toLowerCase().includes(term)
      )
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'name') return getDisplayName(a).localeCompare(getDisplayName(b))
      if (sortBy === 'messages') return b.monthly_user_messages_used - a.monthly_user_messages_used
      if (sortBy === 'cost') return b.monthly_cost_usd - a.monthly_cost_usd
      const aPct = Math.max(a.message_utilization_pct ?? 0, a.cost_utilization_pct ?? 0)
      const bPct = Math.max(b.message_utilization_pct ?? 0, b.cost_utilization_pct ?? 0)
      if (a.credit_limit_exceeded !== b.credit_limit_exceeded) return a.credit_limit_exceeded ? -1 : 1
      return bPct - aPct
    })
  }, [baseMetrics?.users, tier, status, search, sortBy])

  const totalPages = Math.max(1, Math.ceil(filtered.length / USERS_PER_PAGE))

  useEffect(() => {
    setPage(1)
  }, [tier, status, search, sortBy, month, includeInactive])

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  useEffect(() => {
    if (skipPageScrollRef.current) {
      skipPageScrollRef.current = false
      return
    }
    scrollPageToTop(scrollAnchorRef.current)
  }, [page])

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * USERS_PER_PAGE
    return filtered.slice(start, start + USERS_PER_PAGE)
  }, [filtered, page])

  const pageStart = filtered.length === 0 ? 0 : (page - 1) * USERS_PER_PAGE + 1
  const pageEnd = Math.min(page * USERS_PER_PAGE, filtered.length)

  const summary = useMemo(() => computeSummary(filtered), [filtered])
  const byTier = useMemo(
    () => computeByTier(baseMetrics?.users ?? [], baseMetrics?.by_tier ?? []),
    [baseMetrics]
  )
  const monthLabel = formatMonthLabel(baseMetrics?.billing_month ?? month)
  const initialLoad = loading && !baseMetrics
  const selectedTierLabel = TIERS.find((t) => t.value === tier)?.label ?? 'All tiers'
  const selectedStatusLabel = STATUSES.find((s) => s.value === status)?.label ?? 'All statuses'
  const hasFilters = tier !== 'all' || status !== 'all'

  const fieldLabel: React.CSSProperties = { fontSize: 11, color: 'var(--muted)', marginBottom: 4, display: 'block' }
  const sectionTitle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--muted)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }

  return (
    <div ref={scrollAnchorRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...card, padding: '16px 18px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, fontWeight: 500, margin: 0, color: 'var(--text)' }}>
              Credit System
            </h2>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '2px 0 0' }}>{monthLabel} · IST</p>
          </div>
          <AdminPanelButton type="button" onClick={() => void fetchMetrics()} disabled={loading}>
            <RefreshCw size={14} style={loading ? { animation: 'spin .8s linear infinite' } : undefined} />
            Refresh
          </AdminPanelButton>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ minWidth: 140 }}>
            <span style={fieldLabel}>Month</span>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger style={{ height: 36, borderRadius: 8 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ minWidth: 140 }}>
            <span style={fieldLabel}>Tier</span>
            <Select value={tier} onValueChange={(v) => setTier(v as TierFilter)}>
              <SelectTrigger style={{ height: 36, borderRadius: 8 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIERS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div style={{ minWidth: 140 }}>
            <span style={fieldLabel}>Status</span>
            <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
              <SelectTrigger style={{ height: 36, borderRadius: 8 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', paddingBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
            Include inactive
          </label>
        </div>
        {hasFilters && (
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '12px 0 0', padding: '8px 10px', background: 'var(--bg2)', borderRadius: 8 }}>
            Showing {filtered.length} user{filtered.length === 1 ? '' : 's'} · {selectedTierLabel} · {selectedStatusLabel}
            {' · '}
            <button
              type="button"
              onClick={() => { setTier('all'); setStatus('all') }}
              style={{ fontSize: 12, fontWeight: 600, color: 'var(--gold)', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              Clear filters
            </button>
          </p>
        )}
      </div>

      {fetchError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load</AlertTitle>
          <AlertDescription>{fetchError}</AlertDescription>
        </Alert>
      )}

      <div>
        <p style={sectionTitle}>Summary</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, opacity: loading && baseMetrics ? 0.6 : 1 }}>
          {[
            { label: 'Users', value: initialLoad ? '…' : summary.total, sub: `${summary.active} active` },
            { label: 'At limit', value: initialLoad ? '…' : summary.atLimit, sub: 'blocked', accent: '#F87171' },
            { label: 'Near limit', value: initialLoad ? '…' : summary.nearLimit, sub: '≥80%', accent: '#FBBF24' },
            { label: 'Spend', value: initialLoad ? '…' : formatCurrency(summary.totalCost), sub: monthLabel },
          ].map(({ label: lbl, value, sub, accent }) => (
            <div key={lbl} style={{ ...card, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{lbl}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: accent ?? 'var(--text)', marginTop: 2 }}>{value}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p style={sectionTitle}>Tier breakdown</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {byTier.map((t) => {
            const highlighted = tier === t.tier_id
            const color = TIER_COLORS[t.tier_id] ?? 'var(--bd)'
            return (
              <div
                key={t.tier_id}
                style={{
                  ...card,
                  padding: 14,
                  borderLeft: `3px solid ${color}`,
                  ...(highlighted ? { borderColor: color, background: 'var(--bg2)' } : {}),
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, color, marginBottom: 10 }}>
                  {t.tier_label}
                  {highlighted && <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 8, color: 'var(--muted)' }}>filtered</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                  <div><span style={{ color: 'var(--muted)' }}>Users </span><strong>{t.user_count}</strong></div>
                  <div><span style={{ color: 'var(--muted)' }}>Active </span><strong>{t.users_with_usage}</strong></div>
                  <div><span style={{ color: 'var(--muted)' }}>At limit </span><strong style={{ color: t.users_at_limit ? '#F87171' : undefined }}>{t.users_at_limit}</strong></div>
                  <div><span style={{ color: 'var(--muted)' }}>Spend </span><strong>{formatCurrency(t.total_cost_usd)}</strong></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--text)' }}>
            Users ({filtered.length})
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ position: 'relative', width: 200 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--muted)' }} />
              <input
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px 8px 32px',
                  fontSize: 13,
                  border: '1px solid var(--bd)',
                  borderRadius: 8,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
              <SelectTrigger style={{ width: 140, height: 36, borderRadius: 8 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="usage">Highest usage</SelectItem>
                <SelectItem value="messages">Most messages</SelectItem>
                <SelectItem value="cost">Highest spend</SelectItem>
                <SelectItem value="name">Name</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {initialLoad ? (
          <div style={{ ...card, padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ ...card, padding: 32, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            No users match the current filters. Try changing Tier or Status above.
            {hasFilters && (
              <button
                type="button"
                onClick={() => { setTier('all'); setStatus('all') }}
                style={{ display: 'block', margin: '12px auto 0', fontSize: 12, fontWeight: 600, color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {paginatedUsers.map((u) => (
              <div
                key={u.user_id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/user/${u.user_id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/admin/user/${u.user_id}`)}
                style={{
                  ...card,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  borderColor: u.credit_limit_exceeded ? 'rgba(239,68,68,.4)' : 'var(--bd)',
                  background: u.credit_limit_exceeded ? 'rgba(239,68,68,.04)' : 'var(--white)',
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{getDisplayName(u)}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{u.email}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 10, fontWeight: 600 }}>
                    <span style={{ color: TIER_COLORS[u.credit_tier] ?? 'var(--muted)' }}>{u.credit_tier_label}</span>
                    {u.credit_limit_exceeded && <span style={{ color: '#F87171' }}>At limit</span>}
                    {u.status === 'near_limit' && !u.credit_limit_exceeded && <span style={{ color: '#FBBF24' }}>Near limit</span>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                  <UsageBlock
                    label="Messages"
                    used={u.monthly_user_messages_used}
                    limit={u.monthly_message_limit}
                    pct={u.message_utilization_pct}
                    danger={u.message_quota_exceeded}
                    formatValue={(n) => n.toLocaleString()}
                  />
                  <UsageBlock
                    label="Spend"
                    used={u.monthly_cost_usd}
                    limit={u.monthly_cost_usd_limit}
                    pct={u.cost_utilization_pct}
                    danger={u.cost_quota_exceeded}
                    formatValue={formatCurrency}
                  />
                </div>
              </div>
              ))}
            </div>
            {filtered.length > USERS_PER_PAGE && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  marginTop: 16,
                  paddingTop: 16,
                  borderTop: '1px solid var(--bd)',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  Showing {pageStart}–{pageEnd} of {filtered.length}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AdminPanelButton
                    type="button"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={16} /> Prev
                  </AdminPanelButton>
                  <span style={{ fontSize: 13, color: 'var(--muted)', minWidth: 72, textAlign: 'center' }}>
                    Page {page} of {totalPages}
                  </span>
                  <AdminPanelButton
                    type="button"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next <ChevronRight size={16} />
                  </AdminPanelButton>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
